import { useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  ScrollArea,
  cn,
} from '@design-system';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { useWizardEvalRules } from '@/features/wizard-evals/hooks/use-wizard-evals';

interface RuleMultiSelectProps {
  carrier: string;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disallowed?: Set<string>;
}

export function RuleMultiSelect({
  carrier,
  value,
  onChange,
  placeholder = 'Select rules…',
  disallowed,
}: RuleMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data: rules, isFetching } = useWizardEvalRules({ carrier });

  const filteredRules = useMemo(() => {
    if (!rules) return [];
    const lower = search.trim().toLowerCase();
    if (!lower) return rules;
    const tokens = lower.split(/\s+/).filter(Boolean);
    return rules.filter(r => {
      const haystack = `${r.ruleId} ${r.ruleText} ${r.category ?? ''}`.toLowerCase();
      return tokens.every(t => haystack.includes(t));
    });
  }, [rules, search]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const ruleById = useMemo(() => {
    const map = new Map<string, { ruleId: string; ruleText: string }>();
    (rules ?? []).forEach(r => map.set(r.ruleId, { ruleId: r.ruleId, ruleText: r.ruleText }));
    return map;
  }, [rules]);

  const toggleRule = (ruleId: string) => {
    if (disallowed?.has(ruleId)) return;
    const next = selectedSet.has(ruleId)
      ? value.filter(v => v !== ruleId)
      : [...value, ruleId];
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-2">
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className="w-full justify-between font-normal"
        onClick={() => setOpen(true)}
      >
        <span className="truncate text-left">
          {value.length === 0
            ? placeholder
            : `${value.length} rule${value.length === 1 ? '' : 's'} selected`}
        </span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      <Dialog
        open={open}
        onOpenChange={nextOpen => {
          setOpen(nextOpen);
          if (!nextOpen) setSearch('');
        }}
      >
        <DialogContent className="max-h-[85vh] max-w-xl gap-0 overflow-hidden p-0">
          <DialogHeader className="border-b p-4">
            <DialogTitle>Select rules ({carrier})</DialogTitle>
          </DialogHeader>
          <div className="border-b">
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search ruleId, text, or category…"
              autoFocus
              className="border-0 px-4 focus-visible:border-transparent focus-visible:ring-0"
            />
          </div>
          <ScrollArea className="max-h-[50vh]">
            <div className="p-1">
              {isFetching && (
                <p className="p-3 text-center text-sm text-gray-600">Loading rules…</p>
              )}
              {!isFetching && filteredRules.length === 0 && (
                <p className="p-3 text-center text-sm text-gray-600">
                  {rules && rules.length === 0
                    ? `No rules defined for ${carrier} yet.`
                    : 'No matching rules.'}
                </p>
              )}
              {filteredRules.map(rule => {
                const isSelected = selectedSet.has(rule.ruleId);
                const isDisallowed = disallowed?.has(rule.ruleId) ?? false;
                return (
                  <button
                    key={rule.ruleId}
                    type="button"
                    disabled={isDisallowed}
                    onClick={() => toggleRule(rule.ruleId)}
                    className={cn(
                      'flex w-full items-start gap-2 rounded-md px-2 py-2 text-left text-sm',
                      isDisallowed
                        ? 'cursor-not-allowed opacity-40'
                        : 'hover:bg-accent cursor-pointer',
                      isSelected && !isDisallowed && 'bg-accent',
                    )}
                  >
                    <Checkbox
                      checked={isSelected}
                      className="pointer-events-none mt-0.5"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-mono text-xs">{rule.ruleId}</p>
                      <p className="text-muted-foreground truncate text-xs">{rule.ruleText}</p>
                    </div>
                    {isSelected && <Check className="mt-0.5 h-4 w-4 shrink-0 opacity-70" />}
                  </button>
                );
              })}
            </div>
          </ScrollArea>
          <div className="flex items-center justify-between border-t p-3">
            <p className="text-xs text-gray-700">
              {value.length} selected
            </p>
            <div className="flex gap-2">
              {value.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onChange([])}
                >
                  Clear
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                onClick={() => setOpen(false)}
              >
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map(ruleId => {
            const rule = ruleById.get(ruleId);
            return (
              <span
                key={ruleId}
                className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-1.5 py-0.5 font-mono text-[11px] text-gray-700"
                title={rule?.ruleText}
              >
                {ruleId}
                <button
                  type="button"
                  onClick={() => onChange(value.filter(v => v !== ruleId))}
                  className="rounded hover:bg-gray-200"
                  aria-label={`Remove ${ruleId}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
